export interface ApiErrorInit {
  status: number;
  statusText: string;
  url: string;
  body?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  /** Parsed response body, when the server sent one. */
  readonly body: unknown;
  /** Server-derived description, for logs. Never render this to the player. */
  readonly detail: string;

  constructor({ status, statusText, url, body }: ApiErrorInit) {
    super(ApiError.userMessage(status));
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.body = body;
    this.detail = ApiError.describe(status, statusText, body);
  }

  /** 4xx responses will not change on a retry; network and 5xx failures may. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  private static userMessage(status: number): string {
    if (status === 0) return "Couldn't reach the server. Check your connection and try again.";
    if (status === 404) return "This page or resource wasn't found.";
    if (status === 429) return 'Too many requests. Try again in a moment.';
    if (status >= 500) return 'The service is temporarily unavailable. Try again in a moment.';
    return "This request couldn't be completed.";
  }

  private static describe(status: number, statusText: string, body: unknown): string {
    const fromBody =
      typeof body === 'object' && body !== null
        ? ((body as Record<string, unknown>).Message ??
          (body as Record<string, unknown>).message ??
          (body as Record<string, unknown>).error_description ??
          (body as Record<string, unknown>).error)
        : typeof body === 'string' && body.trim() !== ''
          ? body
          : undefined;

    if (typeof fromBody === 'string' && fromBody.trim() !== '') return fromBody;
    if (status === 0) return 'Network request failed';
    return `${status} ${statusText}`.trim();
  }
}
